import { useLocalStorage } from '@bowtie-web/common';
import { useMemo } from 'react';
import { useEffectOnce } from 'react-use';
import { FEMALE, MALE, NON_SMOKER, SMOKER } from '../constants';
import { useAttributeAndQsContext } from '../contexts/AttributeAndQsContext';
import { parseObjectValuesToNumber } from '../helpers';
import { QuotationForm } from '../types';

export const QUOTATION_PARAMS_KEY = 'quotationParams';

const getParamsFromLocalStorage = () => {
  const quotationParamsStr = localStorage.getItem(QUOTATION_PARAMS_KEY);
  if (!quotationParamsStr) return {};
  try {
    const quotationParams = JSON.parse(quotationParamsStr);
    return quotationParams || {};
  } catch (error) {
    return {};
  }
};

export const DEFAULT_QUOTATION_FORM: QuotationForm = {
  gender: MALE,
  smoker: NON_SMOKER,
  age: 25,
  sumAssured: 1000000,
};

/**
 * Returns the quotation form values and a setter function.
 *
 * Values are retrieved/overridden in this order:
 * Querystring -> Local storage -> Form default values
 */
const useQuotationFormValues = () => {
  const qs = useAttributeAndQsContext();

  const initialForm = useMemo(() => {
    const form: QuotationForm = {
      ...DEFAULT_QUOTATION_FORM,
      ...getParamsFromLocalStorage(),
    };

    if (qs.gender && [MALE, FEMALE].includes(qs.gender)) {
      form.gender = qs.gender;
    }
    if (qs.age) {
      const ageNum = parseInt(qs.age, 10);
      form.age = ageNum >= 0 && ageNum <= 80 ? ageNum : form.age;
    }
    if (qs.smoker && [NON_SMOKER, SMOKER].includes(qs.smoker)) {
      form.smoker = qs.smoker;
    }

    if (qs.defaultFormValues) {
      const parsedForm =
        typeof qs.defaultFormValues === 'string'
          ? JSON.parse(qs.defaultFormValues)
          : qs.defaultFormValues;

      const defaultFormValues: Partial<QuotationForm> =
        parseObjectValuesToNumber(parsedForm);

      return { ...form, ...defaultFormValues };
    }

    return form;
  }, [qs.age, qs.defaultFormValues, qs.gender, qs.smoker]);

  const [formValues, setFormValues] = useLocalStorage(
    QUOTATION_PARAMS_KEY,
    initialForm,
    {
      raw: false,
      // Not saving coupon code
      serializer: ({ age, gender, smoker, sumAssured }) =>
        JSON.stringify({ age, gender, smoker, sumAssured }),
      deserializer: serialized => JSON.parse(serialized),
    },
  );

  // Force save new form values due to possibly new querystring values
  useEffectOnce(() => setFormValues(initialForm));

  return [formValues, setFormValues] as const;
};

export default useQuotationFormValues;
